<script>
import moment from "moment";
import axios from "@/utils/axios";
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";

export default {
  inject: ["setLoading"],
  data() {
    return {
      title: "Reports",
      text: "Inventory",
      text1: "Purchase Payment Report",
      activeTab: "transactions", // 'transactions' | 'outstanding'
      filter: {
        from_date: "",
        to_date: "",
        warehouse_id: "",
        vendor_id: "",
        purchase_order_id: "",
        payment_mode: "",
      },
      summary: {},
      data: [],
      warehouses: [],
      vendors: [],
      purchaseOrders: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: [],
      },
      paymentModes: ["Cash", "UPI", "Bank Transfer", "Cheque", "NEFT/RTGS"],
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },
    endpoint() {
      return this.activeTab === "transactions"
        ? "/reports/purchase-payments/transactions"
        : "/reports/purchase-payments/outstanding";
    },
  },

  async mounted() {
    this.getDropdowns();

    const start = moment().subtract(29, "days");
    const end = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date = end.format("YYYY-MM-DD");

    new DateRangePicker(
      this.$refs.dateRangeInput,
      {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: {
          format: "DD/MM/YYYY",
          separator: " - ",
          cancelLabel: "Clear",
        },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value =
          start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
      }
    );

    this.filterReport();
  },

  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.pagination.current_page = 1;
      this.filterReport();
    },

    async filterReport(page = 1) {
      this.setLoading(true);
      try {
        const payload = {
          page,
          per_page: this.pagination.per_page,
          date_from: this.filter.from_date,
          date_to: this.filter.to_date,
        };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.purchase_order_id) payload.purchase_order_id = this.filter.purchase_order_id;
        if (this.filter.payment_mode) payload.payment_mode = this.filter.payment_mode;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`${this.endpoint}?${query_string}`);

        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links,
        };
      } catch (error) {
        this.$swalToast(error.message, "error");
      } finally {
        this.setLoading(false);
      }
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: "csv" };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.purchase_order_id) payload.purchase_order_id = this.filter.purchase_order_id;
        if (this.filter.payment_mode) payload.payment_mode = this.filter.payment_mode;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`${this.endpoint}?${query_string}`, {
          responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `purchase-payment-${this.activeTab}-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast("Failed to export CSV", "error");
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [warehouseRes, vendorRes] = await Promise.all([
          axios.get("/inventory/warehouses/dropdown"),
          axios.get("/inventory/vendors/dropdown"),
        ]);
        this.warehouses = warehouseRes.data.data;
        this.vendors = vendorRes.data.data;
      } catch (error) {
        console.log("Dropdown load error:", error.message);
      }
    },

    async getPurchaseOrders() {
      if (!this.filter.vendor_id) {
        this.purchaseOrders = [];
        return;
      }
      try {
        const response = await axios.get(`/inventory/purchase-orders/dropdown?vendor_id=${this.filter.vendor_id}`);
        this.purchaseOrders = response.data.data;
      } catch (error) {
        console.log("PO dropdown load error:", error.message);
      }
    },

    onVendorChange() {
      this.filter.purchase_order_id = "";
      this.getPurchaseOrders();
      this.filterReport();
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return (
        this.filter.warehouse_id ||
        this.filter.vendor_id ||
        this.filter.purchase_order_id ||
        this.filter.payment_mode ||
        this.filter.from_date ||
        this.filter.to_date
      );
    },

    clearFilter() {
      this.filter.warehouse_id = "";
      this.filter.vendor_id = "";
      this.filter.purchase_order_id = "";
      this.filter.payment_mode = "";
      this.filter.from_date = "";
      this.filter.to_date = "";
      this.purchaseOrders = [];
      if (this.$refs.dateRangeInput) this.$refs.dateRangeInput.value = "";
      this.filterReport();
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString("en-IN", { style: "currency", currency: "INR" });
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'transactions' }" href="javascript:void(0);" @click="switchTab('transactions')">
            <i class="ti ti-receipt me-1"></i>Transactions
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'outstanding' }" href="javascript:void(0);" @click="switchTab('outstanding')">
            <i class="ti ti-alert-triangle me-1"></i>Outstanding
          </a>
        </li>
      </ul>

      <!-- Summary Cards: Transactions -->
      <div class="row g-3 mb-3" v-if="activeTab === 'transactions' && summary && summary.total_transactions">
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Paid</span>
              <span class="fw-bold text-success" style="font-size: 20px">{{ formatCurrency(summary.total_paid) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Transactions</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ summary.total_transactions }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Avg Payment</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ formatCurrency(summary.avg_payment) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Unique Vendors Paid</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ summary.unique_vendors_paid }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Cards: Outstanding -->
      <div class="row g-3 mb-3" v-if="activeTab === 'outstanding' && summary && summary.pos_pending_count != null">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Outstanding</span>
              <span class="fw-bold text-danger" style="font-size: 20px">{{ formatCurrency(summary.total_outstanding) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Received Value</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ formatCurrency(summary.total_received_value) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Advance</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ formatCurrency(summary.total_advance) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Paid</span>
              <span class="fw-bold text-success" style="font-size: 20px">{{ formatCurrency(summary.total_paid) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Pending POs</span>
              <span class="fw-bold text-warning" style="font-size: 20px">{{ summary.pos_pending_count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput" placeholder="dd/mm/yyyy - dd/mm/yyyy" />
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.warehouse_id" :options="warehouses" label="name" :reduce="w => w.id"
                placeholder="Warehouse" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.vendor_id" :options="vendors" label="name" :reduce="v => v.id"
                placeholder="Vendor" :clearable="true" @update:modelValue="onVendorChange()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.purchase_order_id" :options="purchaseOrders" label="po_number" :reduce="p => p.id"
                placeholder="PO Number" :clearable="true" :disabled="!filter.vendor_id"
                @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.payment_mode"
                :options="paymentModes.map(m => ({ label: m, value: m }))" label="label" :reduce="m => m.value"
                placeholder="Payment Mode" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Transactions Table -->
      <div class="row" v-if="activeTab === 'transactions'">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>PO Number</th>
                    <th>Vendor</th>
                    <th>Warehouse</th>
                    <th>Amount Paid</th>
                    <th>Mode</th>
                    <th>Reference No</th>
                    <th>Remarks</th>
                    <th>Document</th>
                    <th>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>{{ formatDate(row.payment_date) }}</td>
                    <td class="fw-bold text-primary">{{ row.purchase_order?.po_number }}</td>
                    <td>{{ row.purchase_order?.vendor?.name || "-" }}</td>
                    <td>{{ row.purchase_order?.warehouse?.name || "-" }}</td>
                    <td class="fw-bold text-success">{{ formatCurrency(row.amount_paid) }}</td>
                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">{{ row.payment_mode }}</span>
                    </td>
                    <td>{{ row.reference_number || "-" }}</td>
                    <td>{{ row.remarks || "-" }}</td>
                    <td>
                      <a v-if="row.document_url" :href="row.document_url" target="_blank" class="text-primary">
                        <i class="ti ti-file-check"></i> View
                      </a>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ row.created_by?.name || "-" }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Outstanding Table -->
      <div class="row" v-if="activeTab === 'outstanding'">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>PO Number</th>
                    <th>Vendor</th>
                    <th>Warehouse</th>
                    <th>Total Value</th>
                    <th>Advance</th>
                    <th>Total Paid</th>
                    <th>Balance Due</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td class="fw-bold text-primary">{{ row.po_number }}</td>
                    <td>{{ row.vendor?.name || "-" }}</td>
                    <td>{{ row.warehouse?.name || "-" }}</td>
                    <td>{{ formatCurrency(row.total_received_value) }}</td>
                    <td>{{ formatCurrency(row.advance_amount) }}</td>
                    <td class="text-success">{{ formatCurrency(row.total_paid) }}</td>
                    <td class="fw-bold text-danger">{{ formatCurrency(row.balance_due) }}</td>
                    <td>{{ formatDate(row.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>